import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { CheckCircle2, ExternalLink, Image, ImagePlus, Loader2, Save, Trash2 } from 'lucide-react';
import { adminAdsAPI } from '../services/adminApi';
import { normalizeAdCollection, normalizeAdRecord } from '../utils/adSlots';

const PanelContainer = styled.div`
  display: grid;
  gap: 20px;
`;

const PanelHeader = styled.div`
  display: grid;
  gap: 8px;
  padding-bottom: 18px;
  border-bottom: 1px solid #e5e7eb;
`;

const PanelTitle = styled.h2`
  color: #1f2937;
  font-size: 1.7rem;
  font-weight: 700;
`;

const PanelDescription = styled.p`
  color: #6b7280;
  max-width: 72ch;
  line-height: 1.7;
`;

const SlotGrid = styled.div`
  display: grid;
  gap: 18px;
`;

const SlotCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 20px;
  display: grid;
  gap: 18px;
`;

const SlotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const SlotHeadingBlock = styled.div`
  display: grid;
  gap: 8px;
`;

const SlotTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SlotTitle = styled.h3`
  color: #111827;
  font-size: 1.1rem;
  font-weight: 700;
`;

const SlotDescription = styled.p`
  color: #64748b;
  line-height: 1.65;
`;

const HintPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${props => props.$active ? '#dcfce7' : '#f1f5f9'};
  color: ${props => props.$active ? '#166534' : '#475569'};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SlotBody = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewCard = styled.div`
  border: 1px solid #dbe3ef;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  display: grid;
  gap: 0;
`;

const PreviewCanvas = styled.div`
  min-height: 210px;
  display: grid;
  place-items: center;
  padding: 18px;
  background:
    radial-gradient(circle at top, rgba(59, 130, 246, 0.08), transparent 55%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 240px;
  object-fit: contain;
  display: block;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  background: white;
`;

const PreviewEmpty = styled.div`
  display: grid;
  justify-items: center;
  gap: 10px;
  text-align: center;
  color: #64748b;

  svg {
    color: #94a3b8;
  }
`;

const PreviewFooter = styled.div`
  padding: 14px 16px 16px;
  border-top: 1px solid #e5e7eb;
  display: grid;
  gap: 10px;
`;

const PreviewUrl = styled.code`
  display: block;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
`;

const PreviewActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const SpinnerIcon = styled(Loader2)`
  animation: ${spin} 0.9s linear infinite;
`;

const buttonStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  ${buttonStyles}
  background: #2c9fa4;
  color: white;

  &:hover:not(:disabled) {
    background: #1c6568;
  }
`;

const SecondaryButton = styled.button`
  ${buttonStyles}
  background: #e2e8f0;
  color: #334155;

  &:hover:not(:disabled) {
    background: #cbd5e1;
  }
`;

const DangerButton = styled.button`
  ${buttonStyles}
  background: #fee2e2;
  color: #b91c1c;

  &:hover:not(:disabled) {
    background: #fecaca;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const FieldGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const FieldLabel = styled.label`
  color: #334155;
  font-size: 0.9rem;
  font-weight: 700;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font: inherit;
  color: #1f2937;
  background: white;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.12);
  }
`;

const ToggleRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #1f2937;
  font-weight: 700;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: #2c9fa4;
  }
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 6px;
`;

const SaveMessage = styled.div`
  color: ${props => props.$error ? '#b91c1c' : '#166534'};
  font-size: 0.9rem;
  line-height: 1.5;
`;

const LoadingState = styled.div`
  min-height: 220px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #6b7280;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
`;

const adsBySlotKey = (records) => Object.fromEntries(records.map((record) => [record.slot_key, record]));

const AdsAdminPanel = () => {
  const [ads, setAds] = useState(() => normalizeAdCollection([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadingBySlot, setUploadingBySlot] = useState({});
  const [savingBySlot, setSavingBySlot] = useState({});
  const [messagesBySlot, setMessagesBySlot] = useState({});
  const fileInputRefs = useRef({});

  const refreshAds = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAdsAPI.getAll();
      setAds(normalizeAdCollection(response.data));
    } catch (refreshError) {
      console.error('Error loading ads:', refreshError);
      setError(refreshError?.response?.data?.error || refreshError?.message || 'Failed to load ads.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAds();
  }, [refreshAds]);

  const setAdFieldValue = useCallback((slotKey, field, value) => {
    setAds((currentAds) => currentAds.map((adRecord) => (
      adRecord.slot_key === slotKey
        ? { ...adRecord, [field]: value }
        : adRecord
    )));

    setMessagesBySlot((currentMessages) => ({
      ...currentMessages,
      [slotKey]: '',
    }));
  }, []);

  const openFilePicker = useCallback((slotKey) => {
    fileInputRefs.current[slotKey]?.click();
  }, []);

  const handleImageUpload = useCallback(async (slotKey, file) => {
    if (!file) {
      return;
    }

    setUploadingBySlot((currentState) => ({
      ...currentState,
      [slotKey]: true,
    }));
    setMessagesBySlot((currentMessages) => ({
      ...currentMessages,
      [slotKey]: '',
    }));

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await adminAdsAPI.uploadImage(slotKey, formData);
      const uploadedImageUrl = String(response.data?.url || '').trim();

      setAds((currentAds) => currentAds.map((adRecord) => (
        adRecord.slot_key === slotKey
          ? {
              ...adRecord,
              image_url: uploadedImageUrl,
            }
          : adRecord
      )));
      setMessagesBySlot((currentMessages) => ({
        ...currentMessages,
        [slotKey]: 'Image uploaded. Save this slot to publish the change.',
      }));
    } catch (uploadError) {
      console.error(`Error uploading ad image for ${slotKey}:`, uploadError);
      setMessagesBySlot((currentMessages) => ({
        ...currentMessages,
        [slotKey]: uploadError?.response?.data?.error || uploadError?.message || 'Failed to upload the image.',
      }));
    } finally {
      setUploadingBySlot((currentState) => ({
        ...currentState,
        [slotKey]: false,
      }));
    }
  }, []);

  const handleSave = useCallback(async (slotKey) => {
    const adRecord = adsBySlotKey(ads)[slotKey];

    if (!adRecord) {
      return;
    }

    setSavingBySlot((currentState) => ({
      ...currentState,
      [slotKey]: true,
    }));
    setMessagesBySlot((currentMessages) => ({
      ...currentMessages,
      [slotKey]: '',
    }));

    try {
      const response = await adminAdsAPI.update(slotKey, {
        image_url: adRecord.image_url,
        click_url: adRecord.click_url,
        alt_text: adRecord.alt_text,
        is_active: adRecord.is_active,
      });
      const savedRecord = normalizeAdRecord(response.data?.data || response.data, slotKey);

      setAds((currentAds) => normalizeAdCollection(
        currentAds.map((currentRecord) => (
          currentRecord.slot_key === slotKey
            ? savedRecord
            : currentRecord
        ))
      ));
      setMessagesBySlot((currentMessages) => ({
        ...currentMessages,
        [slotKey]: response.data?.message || 'Ad saved successfully.',
      }));
    } catch (saveError) {
      console.error(`Error saving ad slot ${slotKey}:`, saveError);
      setMessagesBySlot((currentMessages) => ({
        ...currentMessages,
        [slotKey]: saveError?.response?.data?.error || saveError?.message || 'Failed to save this ad slot.',
      }));
    } finally {
      setSavingBySlot((currentState) => ({
        ...currentState,
        [slotKey]: false,
      }));
    }
  }, [ads]);

  if (loading) {
    return (
      <LoadingState>
        Loading ad placements...
      </LoadingState>
    );
  }

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>Ads</PanelTitle>
        <PanelDescription>
          Manage the ad placements used across MicroPinouts. Each slot lets you upload an image, set the click-through URL, and control whether the ad is live.
        </PanelDescription>
      </PanelHeader>

      {error ? (
        <LoadingState>{error}</LoadingState>
      ) : (
        <SlotGrid>
          {ads.map((adRecord) => {
            const isUploading = Boolean(uploadingBySlot[adRecord.slot_key]);
            const isSaving = Boolean(savingBySlot[adRecord.slot_key]);
            const slotMessage = messagesBySlot[adRecord.slot_key] || '';
            const slotMessageIsError = slotMessage.toLowerCase().includes('failed') || slotMessage.toLowerCase().includes('required') || slotMessage.toLowerCase().includes('valid');

            return (
              <SlotCard key={adRecord.slot_key}>
                <SlotHeader>
                  <SlotHeadingBlock>
                    <SlotTitleRow>
                      <SlotTitle>{adRecord.title}</SlotTitle>
                      <StatusPill $active={adRecord.is_active}>
                        {adRecord.is_active ? 'Active' : 'Inactive'}
                      </StatusPill>
                    </SlotTitleRow>
                    <SlotDescription>{adRecord.description}</SlotDescription>
                  </SlotHeadingBlock>

                  <HintPill>
                    <CheckCircle2 size={14} />
                    {adRecord.recommended_size}
                  </HintPill>
                </SlotHeader>

                <SlotBody>
                  <PreviewCard>
                    <PreviewCanvas>
                      {adRecord.image_url ? (
                        <PreviewImage
                          src={adRecord.image_url}
                          alt={adRecord.alt_text || `${adRecord.title} ad preview`}
                        />
                      ) : (
                        <PreviewEmpty>
                          <Image size={36} />
                          <div>No ad image uploaded yet.</div>
                        </PreviewEmpty>
                      )}
                    </PreviewCanvas>

                    <PreviewFooter>
                      <PreviewUrl>
                        {adRecord.image_url || 'Upload an image to generate an ad asset URL.'}
                      </PreviewUrl>

                      <PreviewActions>
                        <PrimaryButton
                          type="button"
                          onClick={() => openFilePicker(adRecord.slot_key)}
                          disabled={isUploading}
                        >
                          {isUploading ? <SpinnerIcon size={16} /> : <ImagePlus size={16} />}
                          {isUploading ? 'Uploading...' : 'Upload Image'}
                        </PrimaryButton>

                        <SecondaryButton
                          type="button"
                          as="a"
                          href={adRecord.click_url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-disabled={!adRecord.click_url}
                          onClick={(event) => {
                            if (!adRecord.click_url) {
                              event.preventDefault();
                            }
                          }}
                          style={!adRecord.click_url ? { pointerEvents: 'none', opacity: 0.55 } : undefined}
                        >
                          <ExternalLink size={16} />
                          Open Link
                        </SecondaryButton>

                        <DangerButton
                          type="button"
                          onClick={() => {
                            setAdFieldValue(adRecord.slot_key, 'image_url', '');
                            setAdFieldValue(adRecord.slot_key, 'is_active', false);
                          }}
                          disabled={!adRecord.image_url}
                        >
                          <Trash2 size={16} />
                          Remove Image
                        </DangerButton>
                      </PreviewActions>

                      <HiddenFileInput
                        ref={(element) => {
                          fileInputRefs.current[adRecord.slot_key] = element;
                        }}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          handleImageUpload(adRecord.slot_key, file);
                          event.target.value = '';
                        }}
                      />
                    </PreviewFooter>
                  </PreviewCard>

                  <FormGrid>
                    <FieldGroup>
                      <FieldLabel htmlFor={`${adRecord.slot_key}-click-url`}>Click URL</FieldLabel>
                      <Input
                        id={`${adRecord.slot_key}-click-url`}
                        type="url"
                        placeholder="https://example.com/landing-page"
                        value={adRecord.click_url}
                        onChange={(event) => setAdFieldValue(adRecord.slot_key, 'click_url', event.target.value)}
                      />
                    </FieldGroup>

                    <FieldGroup>
                      <FieldLabel htmlFor={`${adRecord.slot_key}-alt-text`}>Alt text</FieldLabel>
                      <Input
                        id={`${adRecord.slot_key}-alt-text`}
                        type="text"
                        placeholder="Brief description of the sponsor graphic"
                        value={adRecord.alt_text}
                        onChange={(event) => setAdFieldValue(adRecord.slot_key, 'alt_text', event.target.value)}
                      />
                    </FieldGroup>

                    <ToggleRow htmlFor={`${adRecord.slot_key}-is-active`}>
                      <input
                        id={`${adRecord.slot_key}-is-active`}
                        type="checkbox"
                        checked={adRecord.is_active}
                        onChange={(event) => setAdFieldValue(adRecord.slot_key, 'is_active', event.target.checked)}
                      />
                      Enable this ad slot
                    </ToggleRow>

                    <SaveRow>
                      <SaveMessage $error={slotMessageIsError}>
                        {slotMessage || ' '}
                      </SaveMessage>

                      <PrimaryButton
                        type="button"
                        onClick={() => handleSave(adRecord.slot_key)}
                        disabled={isSaving || isUploading}
                      >
                        {isSaving ? <SpinnerIcon size={16} /> : <Save size={16} />}
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </PrimaryButton>
                    </SaveRow>
                  </FormGrid>
                </SlotBody>
              </SlotCard>
            );
          })}
        </SlotGrid>
      )}
    </PanelContainer>
  );
};

export default AdsAdminPanel;
